import React, { useMemo, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
} from '@mui/material'

import { Avatar, Icon, IconButton, TextField, Typography } from '~components/internal'
import { type SelectedWorkspace } from '~redux/slices/organizationSlice'
import { type WorkspaceId } from '~types'

interface WorkspaceSwitcherDialogProps {
  open: boolean
  onClose: () => void
  workspaces: SelectedWorkspace[]
  currentWorkspaceId?: WorkspaceId | null
  searchable: boolean
  onSelect: (ws: SelectedWorkspace) => void | Promise<void>
}

const WorkspaceSwitcherDialog: React.FC<WorkspaceSwitcherDialogProps> = ({
  open,
  onClose,
  workspaces,
  currentWorkspaceId,
  searchable,
  onSelect,
}) => {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return workspaces
    return workspaces.filter((ws) => (ws.name ?? '').toLowerCase().includes(q))
  }, [workspaces, query])

  // When not searching, pin the current workspace to the top and list the rest below.
  const isSearching = query.trim() !== ''
  const currentWorkspace = useMemo(
    () => workspaces.find((ws) => ws.id === currentWorkspaceId) ?? null,
    [workspaces, currentWorkspaceId],
  )
  const otherWorkspaces = useMemo(
    () => filtered.filter((ws) => ws.id !== currentWorkspaceId),
    [filtered, currentWorkspaceId],
  )

  const handleClose = (): void => {
    setQuery('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      // Fixed height so the modal doesn't resize while filtering.
      slotProps={{ paper: { sx: { height: 480 } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Change workspace
        <IconButton size='small' aria-label='Close dialog' onClick={handleClose}>
          <Icon name='close' fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {searchable && (
          <TextField
            autoFocus
            fullWidth
            size='small'
            placeholder='Search workspaces'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mb: 1, flexShrink: 0 }}
          />
        )}
        <List disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {!isSearching && currentWorkspace && (
            <>
              <Typography
                variant='caption'
                color='textSecondary'
                sx={{ px: 1.5, pt: 0.5, pb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Current
              </Typography>
              <ListItemButton
                key={currentWorkspace.id}
                selected
                onClick={() => {
                  void onSelect(currentWorkspace)
                }}
                sx={{ borderRadius: 1, gap: 1.5 }}
              >
                <Avatar
                  src={currentWorkspace.icon ?? undefined}
                  alt={currentWorkspace.name ?? 'Workspace'}
                  size='sm'
                />
                <Typography variant='body2' noWrap sx={{ flex: 1 }}>
                  {currentWorkspace.name}
                </Typography>
                <Icon name='check' fontSize='small' color='primary' />
              </ListItemButton>
              {otherWorkspaces.length > 0 && (
                <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                  <Divider />
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ pt: 1, display: 'block', fontWeight: 600 }}
                  >
                    Switch to
                  </Typography>
                </Box>
              )}
            </>
          )}
          {(isSearching ? filtered : otherWorkspaces).map((ws) => (
            <ListItemButton
              key={ws.id}
              selected={ws.id === currentWorkspaceId}
              onClick={() => {
                void onSelect(ws)
              }}
              sx={{ borderRadius: 1, gap: 1.5 }}
            >
              <Avatar src={ws.icon ?? undefined} alt={ws.name ?? 'Workspace'} size='sm' />
              <Typography variant='body2' noWrap>
                {ws.name}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  )
}

export default WorkspaceSwitcherDialog
